/**
 * Loan Officer Data Mappers
 * Transform loan officer API responses to component-friendly format
 */

import type {
  LoanOfficer,
  LoanOfficersResponse,
} from '../api/loanOfficer.service';

/**
 * Mapped loan officer for component use
 */
export interface MappedLoanOfficer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nmls: string;
  photo: string | null;
  location: string;
  fullAddress: string;
  city: string;
  state: string;
  zipCode: string;
  websiteUrl: string | null;
  landingPageUrl: string | null;
  title: string | null;
  bio: string | null;
  specialties: string[];
  languages: string[];
}

/**
 * Mapped loan officers response
 */
export interface MappedLoanOfficersResponse {
  officers: MappedLoanOfficer[];
  total: number;
  pageSize: number;
}

/**
 * Map single loan officer to component format
 * @param officer - Raw loan officer from API
 * @returns Mapped loan officer for components
 */
export const mapLoanOfficerToComponentFormat = (
  officer: LoanOfficer
): MappedLoanOfficer => {
  const fullName = `${officer.firstName} ${officer.lastName}`;
  const location = `${officer.address.city}, ${officer.address.state}`;
  const fullAddress = `${officer.address.line}, ${officer.address.city}, ${officer.address.state} ${officer.address.zipCode}`;

  return {
    id: officer.id,
    name: fullName,
    firstName: officer.firstName,
    lastName: officer.lastName,
    email: officer.email,
    phone: officer.phone,
    nmls: officer.nmlsId,
    photo: officer.photoUrl,
    location,
    fullAddress,
    city: officer.address.city,
    state: officer.address.state,
    zipCode: officer.address.zipCode,
    websiteUrl: officer.websiteUrl,
    landingPageUrl: officer.landingPageUrl,
    title: officer.title,
    bio: officer.bio,
    specialties: officer.specialties,
    languages: officer.languages,
  };
};

/**
 * Map loan officers response to component format
 * @param response - Raw loan officers response from API
 * @returns Mapped response for components
 */
export const mapLoanOfficersToComponentFormat = (
  response: LoanOfficersResponse
): MappedLoanOfficersResponse => {
  return {
    officers: response.loanOfficers.map(mapLoanOfficerToComponentFormat),
    total: response.totalCount,
    pageSize: response.pageSize,
  };
};

export default {
  mapLoanOfficerToComponentFormat,
  mapLoanOfficersToComponentFormat,
};
